import React, { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { LineChartCore } from "./LineChartCore";
import { processChartData } from "./utils";
import { LineChartProps } from "./types";
import { ChartHeader } from "@/components/ChartHeader";
import { ChartSpline, CircleAlert } from "lucide-react";

export const LineChart: React.FC<LineChartProps> = ({
  data,
  interval,
  comparisonData,
  title,
  description,
  className,
  menuOptions,
  gradient,
  tooltipContent,
  colors,
  yAxisRange,
  loading,
  error,
  emptyMessage,
  errorMessage,
  xAxisLabelStrategy = "monthly",
}) => {
  const chartData = useMemo(() => {
    return processChartData(
      data,
      comparisonData,
      interval,
      yAxisRange,
      xAxisLabelStrategy,
    );
  }, [data, comparisonData, interval, yAxisRange, xAxisLabelStrategy]);

  const isEmpty = useMemo(() => {
    if (!data || data.length === 0) return true;
    return data.every(
      (point) =>
        point.value === null || point.value === undefined || isNaN(point.value),
    );
  }, [data]);

  const actionsDisabled = loading || !!error || isEmpty;

  const renderContent = () => {
    if (loading) {
      return <Skeleton className="h-[282px] w-full" />;
    }

    if (error) {
      return (
        <div className="flex h-[282px] w-full flex-col items-center justify-center">
          <div className="flex flex-col items-center space-y-3">
            <CircleAlert className="h-12 w-12 text-muted-foreground" />
            <div className="text-center text-sm text-muted-foreground">
              {typeof error === "string" ? error : errorMessage}
            </div>
          </div>
        </div>
      );
    }

    if (isEmpty) {
      return (
        <div className="flex h-[282px] w-full flex-col items-center justify-center">
          <div className="flex flex-col items-center space-y-3">
            <ChartSpline className="h-12 w-12 text-muted-foreground" />
            <div className="text-center text-sm text-muted-foreground">
              {emptyMessage}
            </div>
          </div>
        </div>
      );
    }

    return (
      <LineChartCore
        data={chartData.transformedData}
        interval={interval}
        yAxisDomain={chartData.yAxisDomain}
        hasComparisonData={!!comparisonData && comparisonData.length > 0}
        className="h-[282px] w-full"
        gradient={gradient}
        tooltipContent={tooltipContent}
        colors={colors}
        xAxisLabelStrategy={xAxisLabelStrategy}
      />
    );
  };

  return (
    <Card className={className} data-testid="card">
      <ChartHeader
        title={title ?? ""}
        subtitle={description ?? ""}
        menuOptions={menuOptions}
        disabled={actionsDisabled}
      />

      <CardContent data-testid="card-content">{renderContent()}</CardContent>
    </Card>
  );
};
